/**
 * Auth Events for Web Application
 *
 * Event emitter for authentication-related events that occur at the API layer.
 * Used to notify the application when auth errors occur that require user action
 * (e.g., redirecting to login page on session expiration).
 */

import { dayjs } from '@personal-trainer-app/shared/src/utils/date';

import type { AuthErrorInterface } from '@personal-trainer-app/shared/src/types/api';

// =============================================================================
// TYPES
// =============================================================================

export type AuthErrorEventType = 'auth:error' | 'auth:session-expired';

export interface AuthEventPayload {
  type: AuthErrorEventType;
  error?: AuthErrorInterface;
  message?: string;
  timestamp: number;
}

type AuthEventCallback = (payload: AuthEventPayload) => void;

// =============================================================================
// AUTH EVENTS EMITTER
// =============================================================================

class AuthEventsEmitter {
  private listeners: Map<AuthErrorEventType, Set<AuthEventCallback>> =
    new Map();

  /**
   * Subscribe to an auth event
   */
  on(event: AuthErrorEventType, callback: AuthEventCallback): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }

  /**
   * Emit an auth event
   */
  emit(event: AuthErrorEventType, payload?: Partial<AuthEventPayload>): void {
    const fullPayload: AuthEventPayload = {
      type: event,
      timestamp: dayjs().valueOf(),
      ...payload,
    };

    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          callback(fullPayload);
        } catch (error) {
          console.error(`[AuthEvents] Error in listener for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Remove all listeners
   */
  clear(): void {
    this.listeners.clear();
  }
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

export const authEvents = new AuthEventsEmitter();
